import { useRef, useState } from 'react';
import { useUploadFile } from '@/hooks/useUploadFile';
import { sanitizeImageUrl } from '@/lib/sanitize';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import { Download, ImagePlus, Loader2, Wand2 } from 'lucide-react';
import { useToast } from '@/hooks/useToast';

export function CoverPicker({
  coverUrl,
  onChange,
  packName,
  memberPubkeys,
}: {
  coverUrl: string;
  onChange: (url: string) => void;
  packName: string;
  memberPubkeys: string[];
}) {
  const { toast } = useToast();
  const { mutateAsync: uploadFile, isPending: isUploading } = useUploadFile();
  const fileRef = useRef<HTMLInputElement>(null);
  const [urlDraft, setUrlDraft] = useState(coverUrl);
  const [isGenerating, setIsGenerating] = useState(false);
  const safePreview = sanitizeImageUrl(coverUrl);

  const applyUrl = () => {
    const safe = sanitizeImageUrl(urlDraft);
    if (!safe) {
      toast({ title: 'That URL won’t work', description: 'Cover images must be https:// links.', variant: 'destructive' });
      return;
    }
    onChange(safe);
  };

  const pickFile = async (file: File | undefined) => {
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast({ title: 'Not an image', description: 'Please choose an image file.', variant: 'destructive' });
      return;
    }
    try {
      const tags = await uploadFile(file);
      const url = tags[0]?.[1];
      const safe = sanitizeImageUrl(url);
      if (!safe) throw new Error('Upload returned an unusable URL.');
      onChange(safe);
      setUrlDraft(safe);
      toast({ title: 'Cover uploaded', description: 'Stored on Blossom and linked to your pack.' });
    } catch (e) {
      toast({
        title: 'Upload failed',
        description: e instanceof Error ? e.message : 'Please try again.',
        variant: 'destructive',
      });
    }
  };

  return (
    <div className="space-y-3">
      <Label>Cover image</Label>
      <div className="relative h-40 overflow-hidden rounded-2xl border bg-muted">
        {safePreview ? (
          <img src={safePreview} alt="Pack cover preview" className="h-full w-full object-cover" />
        ) : (
          <div className="grid h-full place-items-center bg-[linear-gradient(120deg,hsl(84_75%_62%),hsl(200_70%_55%),hsl(280_65%_55%))] p-4 text-center font-display text-2xl text-white">
            {packName.trim() || 'Your pack title'}
          </div>
        )}
      </div>
      <div className="flex gap-2">
        <Input
          value={urlDraft}
          onChange={(e) => setUrlDraft(e.target.value)}
          placeholder="https://… image link"
          inputMode="url"
          className="rounded-full"
        />
        <Button type="button" variant="secondary" className="shrink-0 rounded-full" onClick={applyUrl}>
          Use link
        </Button>
      </div>
      <div className="flex flex-wrap gap-2">
        <input
          ref={fileRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={(e) => void pickFile(e.target.files?.[0])}
        />
        <Button type="button" variant="outline" className="rounded-full" disabled={isUploading} onClick={() => fileRef.current?.click()}>
          {isUploading ? <Loader2 className="size-4 animate-spin" /> : <ImagePlus className="size-4" />}
          {isUploading ? 'Uploading…' : 'Upload image'}
        </Button>
        <CoverGenerator
          packName={packName}
          memberPubkeys={memberPubkeys}
          generating={isGenerating}
          setGenerating={setIsGenerating}
          onGenerated={(url) => {
            onChange(url);
            setUrlDraft(url);
          }}
        />
        {coverUrl ? (
          <Button type="button" variant="ghost" className="rounded-full" onClick={() => { onChange(''); setUrlDraft(''); }}>
            Remove
          </Button>
        ) : null}
      </div>
      <p className="text-xs text-muted-foreground">
        Paste an https link or upload — uploads are stored on Blossom, so the cover loads fast every refresh.
      </p>
    </div>
  );
}

/**
 * Option 2 cover maker: draws a 1200×630 flyer in the browser (no server),
 * with download + one-click Blossom upload.
 */
function CoverGenerator({
  packName,
  memberPubkeys,
  generating,
  setGenerating,
  onGenerated,
}: {
  packName: string;
  memberPubkeys: string[];
  generating: boolean;
  setGenerating: (v: boolean) => void;
  onGenerated: (objectUrlOrBlossomUrl: string) => void;
}) {
  const { toast } = useToast();
  const { mutateAsync: uploadFile, isPending: isUploading } = useUploadFile();
  const [lastBlob, setLastBlob] = useState<Blob | null>(null);

  const draw = async (): Promise<Blob> => {
    const W = 1200;
    const H = 630;
    const canvas = document.createElement('canvas');
    canvas.width = W;
    canvas.height = H;
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('Canvas is not available in this browser.');

    // Background
    const bg = ctx.createLinearGradient(0, 0, W, H);
    bg.addColorStop(0, '#101408');
    bg.addColorStop(0.55, '#1d2b12');
    bg.addColorStop(1, '#0b3b3f');
    ctx.fillStyle = bg;
    ctx.fillRect(0, 0, W, H);

    // Lime glow blobs
    const blob = (x: number, y: number, r: number, color: string) => {
      const g = ctx.createRadialGradient(x, y, 0, x, y, r);
      g.addColorStop(0, color);
      g.addColorStop(1, 'rgba(0,0,0,0)');
      ctx.fillStyle = g;
      ctx.fillRect(x - r, y - r, r * 2, r * 2);
    };
    blob(200, 120, 320, 'rgba(190,242,100,0.5)');
    blob(1020, 520, 340, 'rgba(56,189,248,0.35)');

    // Title
    ctx.fillStyle = '#f4f6ec';
    ctx.font = '600 92px Inter, system-ui, sans-serif';
    const title = (packName.trim() || 'Untitled Pack').slice(0, 42);
    ctx.fillText(title, 80, 250, W - 160);

    ctx.fillStyle = 'rgba(244,246,236,0.75)';
    ctx.font = '500 40px Inter, system-ui, sans-serif';
    ctx.fillText(`A Follow Party pack · ${memberPubkeys.length} ${memberPubkeys.length === 1 ? 'person' : 'people'}`, 80, 320, W - 160);

    ctx.fillStyle = '#bef264';
    ctx.font = '700 36px Inter, system-ui, sans-serif';
    ctx.fillText('FOLLOW PARTY', 80, 540);

    // Avatar stack (first 8) — loaded via kind 0 pictures, initials fallback.
    const shown = memberPubkeys.slice(0, 8);
    let x = 80;
    const y = 380;
    const R = 44;
    for (const pk of shown) {
      const url = await resolveAvatar(pk);
      ctx.save();
      ctx.beginPath();
      ctx.arc(x + R, y + R, R, 0, Math.PI * 2);
      ctx.clip();
      if (url) {
        try {
          const img = await loadImage(url);
          ctx.drawImage(img, x, y, R * 2, R * 2);
        } catch {
          paintInitials(ctx, pk, x, y, R);
        }
      } else {
        paintInitials(ctx, pk, x, y, R);
      }
      ctx.restore();
      ctx.strokeStyle = '#0e1206';
      ctx.lineWidth = 6;
      ctx.beginPath();
      ctx.arc(x + R, y + R, R, 0, Math.PI * 2);
      ctx.stroke();
      x += R * 1.35;
    }

    return await new Promise<Blob>((resolve, reject) => {
      canvas.toBlob((b) => (b ? resolve(b) : reject(new Error('Could not render image.'))), 'image/png');
    });
  };

  const generate = async (uploadAfter: boolean) => {
    setGenerating(true);
    try {
      const blob = await draw();
      setLastBlob(blob);
      if (uploadAfter) {
        const file = new File([blob], 'follow-party-cover.png', { type: 'image/png' });
        const tags = await uploadFile(file);
        const url = sanitizeImageUrl(tags[0]?.[1]);
        if (!url) throw new Error('Upload returned an unusable URL.');
        onGenerated(url);
        toast({ title: 'Cover generated + uploaded', description: 'Saved to Blossom and set as your cover.' });
      } else {
        const objectUrl = URL.createObjectURL(blob);
        onGenerated(objectUrl);
        toast({ title: 'Cover preview ready', description: 'Download it, or upload it to Blossom.' });
      }
    } catch (e) {
      toast({
        title: 'Could not generate cover',
        description: e instanceof Error ? e.message : 'Please try again.',
        variant: 'destructive',
      });
    } finally {
      setGenerating(false);
    }
  };

  return (
    <Card className="w-full border-dashed bg-transparent shadow-none">
      <CardContent className="flex flex-wrap items-center gap-2 p-3">
        <span className="flex items-center gap-1.5 text-sm font-medium">
          <Wand2 className="size-4" />
          Auto-make cover
        </span>
        <Button type="button" size="sm" variant="secondary" className="rounded-full" disabled={generating} onClick={() => void generate(false)}>
          {generating ? <Loader2 className="size-4 animate-spin" /> : null}
          Preview
        </Button>
        <Button type="button" size="sm" className="rounded-full" disabled={generating || isUploading} onClick={() => void generate(true)}>
          Generate + use
        </Button>
        <Button
          type="button"
          size="sm"
          variant="ghost"
          className="rounded-full"
          disabled={!lastBlob}
          onClick={() => {
            if (!lastBlob) return;
            const a = document.createElement('a');
            a.href = URL.createObjectURL(lastBlob);
            a.download = 'follow-party-cover.png';
            a.click();
          }}
        >
          <Download className="size-4" />
          Download PNG
        </Button>
        <span className="w-full text-xs text-muted-foreground">
          Drawn in your browser — no server needed. {memberPubkeys.length} avatars included.
        </span>
      </CardContent>
    </Card>
  );
}

async function resolveAvatar(pubkey: string): Promise<string | null> {
  // Avatar pictures are resolved lazily by the editor (HydrateName) and baked
  // into initials here; remote images are attempted only when fetchable.
  // Keep this hook for future kind-0 picture injection without changing draw().
  void pubkey;
  return null;
}

function paintInitials(ctx: CanvasRenderingContext2D, pubkey: string, x: number, y: number, r: number) {
  let hash = 0;
  for (let i = 0; i < pubkey.length; i++) hash = (hash * 31 + pubkey.charCodeAt(i)) >>> 0;
  ctx.fillStyle = `hsl(${hash % 360} 60% 55%)`;
  ctx.fillRect(x, y, r * 2, r * 2);
  ctx.fillStyle = '#fff';
  ctx.font = `700 ${r}px Inter, sans-serif`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(pubkey.slice(0, 2).toUpperCase(), x + r, y + r + 2);
  ctx.textAlign = 'left';
  ctx.textBaseline = 'alphabetic';
}

function loadImage(url: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('avatar failed'));
    img.src = url;
  });
}
